import { QueryClient, QueryClientProvider, QueryKey, useQuery } from '@tanstack/react-query';
import React from 'react';

import { Projects } from '../resources/Projects';
import { ProjectTreeService } from './ProjectTreeService';

export const projectQueryKeys = {
    all: () => ['projects'] as const,
    lists: () => [...projectQueryKeys.all(), 'list'] as const,
    list: (siteId: string) => [...projectQueryKeys.lists(), { siteId }] as const,
    destinationProjects: (siteId: string) => [...projectQueryKeys.list(siteId), 'destinations'] as const,
    details: () => [...projectQueryKeys.all(), 'detail'] as const,
    detail: (siteId: string, id: string) => [...projectQueryKeys.details(), siteId, id] as const,
};

export const projectQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            cacheTime: Infinity,
        }
    }
});

export function ProjectQueryProvider({ children }: { readonly children?: React.ReactNode }) {
    return (
        <QueryClientProvider client={projectQueryClient}>
            {children}
        </QueryClientProvider>
    );
}

export function invalidateProjectQueries(key: QueryKey) {
    projectQueryClient.invalidateQueries(key);
}

export const ProjectHooks = {
    useDestinationProjects,
    useProjectDetail,
    invalidateProjectQueries,
};

function useDestinationProjects(siteId: string) {
    const { isLoading, data } = useQuery({
        queryKey: projectQueryKeys.destinationProjects(siteId),
        queryFn: getAllDestinationProjects,
    });

    return {
        isLoading,
        destinationProjects: data
    };
}

function useProjectDetail(siteId: string, projectId: string) {
    const { isLoading, error, data } = useQuery({
        queryKey: projectQueryKeys.detail(siteId, projectId),
        queryFn: () => {
            return Projects.getById(projectId);
        },
    });

    return {
        isLoading,
        error,
        projectDetail: data
    };
}

function getAllDestinationProjects() {
    return ProjectTreeService.instance.getAllProjects();
}